/**
 * Dependency-free CSV export builders for the GroupDetail dashboard.
 *
 * Two concerns live here and are deliberately kept apart:
 *
 *   1. **Pure builders + guard** (`buildRosterCsv`, `buildFinanceCsv`,
 *      `exportFinanceCsv`) — turn already-localized headers + plain data
 *      rows into CSV text with no side effects. These are what the
 *      property tests exercise (Properties 12/13: export content + the
 *      finance-permission guard).
 *   2. **Side-effecting download helpers** (`downloadCsv`,
 *      `downloadRosterCsv`, `downloadFinanceCsv`) — turn CSV text into a
 *      Blob and trigger a browser download. These touch the DOM and are
 *      validated with example/smoke tests, never with the property tests.
 *
 * The util is intentionally i18next-free: callers pass in localized header
 * objects so the builders stay pure and deterministic.
 */

import type { ICurrencyMoneyModel } from 'core/utils/groupMoney';

/**
 * Per-currency Money_Model figures consumed by the finance export.
 *
 * The canonical `ICurrencyMoneyModel` is owned by `core/utils/groupMoney.ts`
 * (spec task 3.1) and consumed here directly. It is re-exported so callers that
 * reach the finance export through this module (the FinancesTab export button,
 * the export property tests) get the type from one place without a second
 * import.
 */
export type { ICurrencyMoneyModel };

/**
 * One roster line for the export. Carries the already-derived display
 * fields so the builder stays pure: callers map an `IGroupRosterRow`
 * (`student.user` name parts, `budgetAmount`/`paidAmount`, …) onto this
 * flat shape, INCLUDING `debt` — the builder must not re-derive it.
 */
export interface IRosterExportRow {
  studentName: string;
  agreementTitle: string;
  currency: string;
  budget: number;
  paid: number;
  /**
   * Σ per-instalment remainder from the server (ТЗ 0029). Carried, not computed:
   * `max(0, budget − paid)` disagrees with the screen the file was exported
   * from — an overpaid instalment cancels a debt owed on another one, so the
   * exported debtor list silently loses debtors.
   */
  debt: number;
  /**
   * Group name. Optional/additive: the Student roster export (Req 21.3) includes
   * a group column, while the Group dashboard roster export omits it. The column
   * is emitted only when `IRosterCsvHeaders.group` is provided.
   */
  group?: string;
}

/** Localized column headers for the roster export (order is fixed by the builder). */
export interface IRosterCsvHeaders {
  studentName: string;
  agreementTitle: string;
  currency: string;
  budget: string;
  paid: string;
  debt: string;
  /** When present, a group column is emitted after agreement title (Req 21.3). */
  group?: string;
}

/** Localized column headers for the finance export (order is fixed by the builder). */
export interface IFinanceCsvHeaders {
  currency: string;
  planned: string;
  issued: string;
  paid: string;
  /** «Долг» — просроченная часть плана (ТЗ 0036). */
  debt: string;
  /** «Предстоит» — ДЕНЬГИ по взносам, срок которых ещё не наступил. */
  upcoming: string;
  /** Счётчик просроченных взносов — штуки, не деньги. */
  overdueCount: string;
  /** Счётчик будущих взносов — штуки, не деньги. */
  upcomingCount: string;
}

/**
 * UTF-8 byte-order mark. Prefixing the CSV with the BOM makes Excel open the
 * file as UTF-8 instead of the local ANSI codepage, so non-ASCII names
 * (Cyrillic/Polish) render correctly.
 */
const UTF8_BOM = '\uFEFF';

/** RFC-4180 record separator. Excel is happiest with CRLF. */
const ROW_SEP = '\r\n';

/**
 * Escape a single CSV field per RFC 4180: a field containing a comma, double
 * quote, CR, or LF is wrapped in double quotes, and any embedded double quote
 * is doubled. Everything else is passed through untouched.
 */
const escapeCsvField = (value: string): string =>
  /[",\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/**
 * Render a number for a data cell. Non-finite values (NaN/Infinity/`null`)
 * become an empty cell so the figure reads as "absent" rather than "NaN".
 */
const numberCell = (value: number | null | undefined): string =>
  typeof value === 'number' && Number.isFinite(value) ? String(value) : '';

/** Coerce a possibly-stringy/nullish numeric into a finite number (0 fallback). */
const toFinite = (value: number | null | undefined): number => {
  const num = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(num) ? num : 0;
};

/**
 * Serialize a matrix of already-stringified cells into CSV text, prefixed with
 * the UTF-8 BOM. Each inner array is one record; cells are escaped per RFC 4180.
 */
export const toCsv = (rows: ReadonlyArray<ReadonlyArray<string>>): string =>
  UTF8_BOM + rows.map((row) => row.map(escapeCsvField).join(',')).join(ROW_SEP);

/**
 * Build roster CSV text: the header line followed by one data line per input
 * row, with columns student name, agreement title, currency, budget, paid and
 * debt. `debt` comes from the row as the screen showed it. Pure — no guard,
 * no DOM.
 */
export const buildRosterCsv = (
  rows: ReadonlyArray<IRosterExportRow>,
  headers: IRosterCsvHeaders,
): string => {
  const withGroup = headers.group !== undefined;

  const headerLine: string[] = [
    headers.studentName,
    headers.agreementTitle,
    ...(withGroup ? [headers.group as string] : []),
    headers.currency,
    headers.budget,
    headers.paid,
    headers.debt,
  ];

  const dataLines = rows.map((row) => {
    const budget = toFinite(row.budget);
    const paid = toFinite(row.paid);
    const debt = toFinite(row.debt);

    return [
      row.studentName,
      row.agreementTitle,
      ...(withGroup ? [row.group ?? ''] : []),
      row.currency,
      numberCell(budget),
      numberCell(paid),
      numberCell(debt),
    ];
  });

  return toCsv([headerLine, ...dataLines]);
};

/**
 * Build finance CSV text: the header line followed by one data line per
 * currency in the Money_Model, carrying that currency's planned, issued, paid,
 * debt, overdue, and upcoming figures. A currency with no invoice data leaves
 * the `issued` cell empty. Pure — no guard, no DOM.
 */
export const buildFinanceCsv = (
  model: ReadonlyArray<ICurrencyMoneyModel>,
  headers: IFinanceCsvHeaders,
): string => {
  const headerLine: string[] = [
    headers.currency,
    headers.planned,
    headers.issued,
    headers.paid,
    headers.debt,
    headers.upcoming,
    headers.overdueCount,
    headers.upcomingCount,
  ];

  const dataLines = model.map((row) => [
    row.currency,
    numberCell(row.planned),
    row.hasInvoiceData ? numberCell(row.issued) : '',
    numberCell(row.paid),
    numberCell(row.debt),
    // ТЗ 0036 — деньги «предстоит» СВОЕЙ колонкой. Без неё файл перестал
    // сходиться: «Долг» стал только просроченным, а остальная часть плана
    // никуда не выгружалась, и `запланировано ≠ оплачено + долг`.
    numberCell(row.upcoming),
    numberCell(row.overdueCount),
    numberCell(row.upcomingCount),
  ]);

  return toCsv([headerLine, ...dataLines]);
};

/**
 * Guarded finance-export entrypoint. Returns the finance CSV text only when
 * the caller is permitted to view finance data; when `canExportFinance` is
 * `false` it returns `null` and produces no output, so no file can be
 * generated. Pure — the permission guard is testable without the DOM
 * (Property 13). Roster export has no equivalent guard.
 */
export const exportFinanceCsv = (
  model: ReadonlyArray<ICurrencyMoneyModel>,
  headers: IFinanceCsvHeaders,
  canExportFinance: boolean,
): string | null => {
  if (!canExportFinance) {
    return null;
  }

  return buildFinanceCsv(model, headers);
};

/* -------------------------------------------------------------------------- */
/* Side-effecting browser download helpers (DOM — not property-tested).        */
/* -------------------------------------------------------------------------- */

/**
 * Trigger a browser download of `csv` as `filename`. Wraps the text in a
 * `text/csv` Blob, creates a temporary object URL + anchor, clicks it, and
 * revokes the URL. No-op outside a browser environment (no `document`).
 */
export const downloadCsv = (csv: string, filename: string): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};

/**
 * Build and download the roster CSV. No permission guard — roster export is
 * available to any operator who can see the roster.
 */
export const downloadRosterCsv = (
  rows: ReadonlyArray<IRosterExportRow>,
  headers: IRosterCsvHeaders,
  filename: string,
): void => {
  downloadCsv(buildRosterCsv(rows, headers), filename);
};

/**
 * Guarded build-and-download for the finance CSV. Delegates the permission
 * decision to `exportFinanceCsv`; when denied, no file is generated and the
 * helper reports `false`. Returns `true` once a download has been triggered.
 */
export const downloadFinanceCsv = (
  model: ReadonlyArray<ICurrencyMoneyModel>,
  headers: IFinanceCsvHeaders,
  canExportFinance: boolean,
  filename: string,
): boolean => {
  const csv = exportFinanceCsv(model, headers, canExportFinance);

  if (csv === null) {
    return false;
  }

  downloadCsv(csv, filename);
  return true;
};
